import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  CardTitle,
  CardSubtitle,
  CardText,
  Badge,
  Alert,
  Spinner,
  Button,
} from "reactstrap";
import CategoryServices from "../../../services/OrderingServices/CategoryService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import StatusBadge from "../../../Components/Common/Badge";
import { Triangle } from "react-loader-spinner";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
// import CustomDataTable from "../../../Components/Common/CustomDataTable";

const CategoryDetail = () => {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const emptyStateIcon = "ri-emotion-unhappy-line";
  const emptyStateMessage = "There are no products in this category.";
  useEffect(() => {
    const fetchCategoryDetail = async () => {
      setLoading(true);
      setProductsLoading(true);

      try {
        const [categoryRes, productsRes] = await Promise.all([
          CategoryServices.viewcategory(id),
          CategoryServices.categoryProducts(id),
        ]);

        setCategory(categoryRes);
        setProducts(productsRes || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching category details:", err);
        setError("Failed to load category details. Please try again later.");
      } finally {
        setLoading(false);
        setProductsLoading(false);
      }
    };

    fetchCategoryDetail();
  }, [id]);

  const handlePaginationChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        name: "S.N.",
        selector: (row, index) => (currentPage - 1) * perPage + index + 1,
        width: "70px",
        center: true,
      },
      {
        name: "Product Name",
        selector: (row) => row.name,
        sortable: true,
        minWidth: "200px",
        cell: (row) => (
          <div className="d-flex align-items-center">
            <div>
              <div className="fw-semibold">{row.name}</div>
            </div>
          </div>
        ),
      },
      {
        name: "Unit",
        selector: (row) => row.unitName || "N/A",
        sortable: true,
        width: "120px",
      },
      {
        name: "Price",
        selector: (row) => row.price,
        sortable: true,
        width: "130px",
        cell: (row) => (
          <div>
            <div className="">
              Rs. <NumberWithCommas number={row.discountedPrice ?? row.price} />
            </div>
            {row.discountedPrice && row.discountedPrice < row.price && (
              <div className="text-muted text-decoration-line-through small">
                Rs. <NumberWithCommas number={row.price} />
              </div>
            )}
          </div>
        ),
      },
      {
        name: "Discount",
        selector: (row) => row.discountName,
        width: "150px",
        cell: (row) =>
          row.discountName ? (
            <Badge color="success" pill>
              {row.discountName}
            </Badge>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        name: "Status",
        selector: (row) => row.isHidden,
        width: "120px",
        center: true,
        cell: (row) => (
          // <Badge color={row.isHidden ? "warning" : "success"} pill>
          //   {row.isHidden ? "Hidden" : "Visible"}
          // </Badge>
          <StatusBadge status={row.isHidden} pill={true} type="visible" />
        ),
      },
    ],
    [currentPage, perPage]
  );

  const tableStyles = {
    table: {
      style: {
        borderRadius: "8px",
        overflow: "hidden",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
        minHeight: "52px",
        fontWeight: "600",
        fontSize: "14px",
      },
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        color: "#495057",
      },
    },
    rows: {
      style: {
        minHeight: "60px",
        "&:hover": {
          backgroundColor: "#f8f9fa",
          cursor: "pointer",
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        fontSize: "14px",
      },
    },
  };

  // const LoadingState = () => (
  //   <div className="text-center py-5">
  //     <Spinner color="primary" />
  //     <p className="mt-2 mb-0">Loading products...</p>
  //   </div>
  // );

  const EmptyState = () => (
    <div className="text-center my-5 w-100">
      <i className={`${emptyStateIcon} display-4 text-secondary`} />
      <h5 className="fw-semibold text-secondary mt-3">No Data Found</h5>
      <p className="text-muted mb-0">{emptyStateMessage}</p>
    </div>
  );

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }} // almost full viewport height
      >
        <div className="text-center">
          <Triangle
            visible={true}
            height="80"
            width="80"
            color="#5B71B9"
            ariaLabel="triangle-loading"
          />
          <p className="mt-3 text-muted">Loading category details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="danger" className="my-4">
        <h5 className="alert-heading">Error!</h5>
        <p className="mb-0">{error}</p>
      </Alert>
    );
  }

  if (!category) {
    return (
      <Alert color="warning" className="my-4">
        <h5 className="alert-heading">Category Not Found</h5>
        <p className="mb-0">
          The requested category could not be found or may have been removed.
        </p>
      </Alert>
    );
  }

  const breadcrumbItems = [{ title: "Back To List", link: "/category-list" }];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          pageTitle="Categories"
          title="Category Detail"
          breadcrumbItems={breadcrumbItems}
        />

        <Row className="mb-4">
          {/* Category Image Column */}
          {category.imageUrl && (
            <Col lg={5} xl={4}>
              <Card className="border-0 shadow-sm">
                <div
                  className="position-relative overflow-hidden"
                  style={{ borderRadius: "8px" }}
                >
                  <img
                    src={
                      category.displayImage?.getCDNUrl() ||
                      `/default-category.jpg`
                    }
                    alt={category.name}
                    className="img-fluid"
                    style={{
                      width: "100%",
                      height: "350px",
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={(e) => {
                      if (!e.target.dataset.error) {
                        e.target.dataset.error = "true";
                        e.target.src = "/default-category.jpg";
                      }
                    }}
                  />
                </div>
              </Card>
            </Col>
          )}

          {/* Category Details Column */}
          <Col lg={category.imageUrl ? 7 : 12} xl={category.imageUrl ? 8 : 12}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div>
                    <CardTitle tag="h2" className="mb-2 fw-bold">
                      {category.name}
                    </CardTitle>
                    <CardSubtitle className="text-muted d-flex align-items-center gap-2">
                      <i className="ri-folder-line"></i>
                      <span>
                        Parent Category:{" "}
                        <span className="fw-semibold text-dark">
                          {category.parentName || "None"}
                        </span>
                      </span>
                    </CardSubtitle>
                  </div>
                  {/* <Badge
                    color={category.isActive ? "success" : "danger"}
                    pill
                    className="px-3 py-2"
                    style={{ fontSize: "14px" }}
                  >
                    {category.isActive ? "Active" : "Inactive"}
                  </Badge> */}

                  <StatusBadge
                    status={category.isActive}
                    pill={true}
                    type="status"
                  />
                </div>
                <Row className="">
                  {/* Description */}
                  <Col md={8}>
                    <Card className="shadow-sm border-0">
                      <CardBody className=" rounded-3">
                        <h6 className="fw-semibold d-flex align-items-center gap-2 mb-3">
                          <i className="ri-file-text-line text-primary fs-5"></i>
                          Description
                        </h6>

                        <CardText
                          className="text-muted"
                          style={{ lineHeight: "1.9" }}
                        >
                          {category.description || "No description available."}
                        </CardText>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Stats */}
                  <Col md={4}>
                    <Row className="">
                      {/* Total Products */}
                      <Col md={6}>
                        <Card className="text-center shadow-sm border-1">
                          <CardBody>
                            <div className="d-flex justify-content-center align-items-center gap-2 text-muted small mb-1">
                              Total Products
                            </div>
                            <h3 className="fw-bold text-primary mb-0">
                              {products.length}
                            </h3>
                          </CardBody>
                        </Card>
                      </Col>

                      {/* Visible Products */}
                      <Col md={6}>
                        <Card className="text-center shadow-sm border-1">
                          <CardBody>
                            <div className="d-flex justify-content-center align-items-center gap-2 text-muted small mb-1">
                              Visible Products
                            </div>
                            <h3 className="fw-bold text-success mb-0">
                              {products.filter((p) => !p.isHidden).length}
                            </h3>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Products DataTable */}
        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <CardTitle
                    tag="h4"
                    className="mb-0 fw-semibold d-flex align-items-center gap-2"
                  >
                    Products in this Category
                  </CardTitle>
                  <Badge color="primary" pill className="px-3 py-2">
                    {products.length}{" "}
                    {products.length === 1 ? "Product" : "Products"}
                  </Badge>
                </div>

                <CustomDataTable
                  responsive
                  striped
                  pagination
                  fixedHeader
                  highlightOnHover
                  pointerOnHover
                  persistTableHead
                  customStyles={tableStyles}
                  progressPending={productsLoading}
                  // progressComponent={<LoadingState />}
                  paginationTotalRows={products.length}
                  onChangePage={handlePaginationChange}
                  onChangeRowsPerPage={handleRowsPerPageChange}
                  columns={columns}
                  progressComponent={
                    <div className="my-3">
                      <Triangle
                        visible={true}
                        height="80"
                        width="80"
                        color="#5B71B9"
                        ariaLabel="triangle-loading"
                      />
                      <h5 className="mt-1">Loading...</h5>
                    </div>
                  }
                  data={products}
                  paginationPerPage={perPage}
                  noDataComponent={<EmptyState />}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CategoryDetail;
